/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { debounce } from 'lodash';
import { View } from 'ol';
import { unByKey } from 'ol/Observable';
import { transformExtent } from 'ol/proj';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryGetWMSGetCapabilities } from '@opengeoweb/webmap-react';
import { InfoDialogWMSMap } from './InfoDialogWMSMap';
import { dataExplorerActions } from '../../store';

interface InfoDialogWMSMapConnectProps {
  geometry: {
    type: string;
    coordinates: number[][][] | number[][][][];
  };
  links: {
    href: string;
    rel: string;
    title?: string;
    type: string;
  }[];
}

interface DataLink {
  href: string;
  rel: string;
  title?: string;
  type: string;
}

const view = new View({
  projection: 'EPSG:3857',
  zoom: 0,
  center: [0, 0],
});

// Normalize longitude values so that they are between -180 and 180 degrees
const normalizeLongitude = (lon: number): number => {
  let normalized = lon;

  while (normalized < -180) {
    normalized += 360;
  }
  while (normalized > 180) {
    normalized -= 360;
  }

  return normalized;
};

const InfoDialogWMSMapConnect: React.FC<InfoDialogWMSMapConnectProps> = ({
  geometry,
  links,
}: InfoDialogWMSMapConnectProps) => {
  const dispatch = useDispatch();
  const [dataLink, setDataLink] = React.useState<string>('');

  const { data: getCapabilitiesJSON } = useQueryGetWMSGetCapabilities(dataLink);

  const debouncedSetBbox = useMemo(
    () =>
      debounce(() => {
        const zoom = view.getZoom();
        // Some areas are cut out around the edges of the map even when zoomed out
        // So at low zoom levels, set the bbox to the whole world
        if (zoom !== undefined && zoom < 0.1) {
          dispatch(
            dataExplorerActions.setWMSbox({ wmsBox: [-180, -90, 180, 90] }),
          );
        } else {
          const extent = view.calculateExtent();
          const transformedExtent = transformExtent(
            extent,
            view.getProjection(),
            'EPSG:4326',
          );

          // The map can be panned left or right beyond the -180/180 longitude range, so values need to be normalized
          transformedExtent[0] = normalizeLongitude(transformedExtent[0]);
          transformedExtent[2] = normalizeLongitude(transformedExtent[2]);

          dispatch(
            dataExplorerActions.setWMSbox({ wmsBox: transformedExtent }),
          );
        }
      }, 1000),
    [dispatch],
  );

  useEffect(() => {
    const dataLink = links.find((link) => link.rel === 'data') as
      | DataLink
      | undefined;
    if (dataLink) {
      setDataLink(dataLink.href);
    }
  }, [links]);

  const setWMSbox = useCallback((): void => {
    debouncedSetBbox();
  }, [debouncedSetBbox]);

  useEffect(() => {
    if (!view) {
      return (): void => {};
    }

    const eventKey = view.on('change', () => {
      setWMSbox();
    });

    return (): void => {
      unByKey(eventKey);
    };
  }, [setWMSbox]);

  return (
    dataLink &&
    getCapabilitiesJSON && (
      <InfoDialogWMSMap
        dataLink={dataLink}
        geometry={geometry}
        getCapabilitiesJSON={getCapabilitiesJSON}
        view={view}
      />
    )
  );
};

export default InfoDialogWMSMapConnect;
